// frontend/src/hooks/useTasks.ts
import { createElement, useCallback, type ComponentType } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Button, Group, Text } from '@mantine/core';

// Mantine v8 polymorphic components have complex generic types that
// are incompatible with React.createElement's type expectations.
// Cast to simple ComponentType to avoid TS2769 errors in notification messages.
const TextC = Text as ComponentType<Record<string, unknown>>;
const ButtonC = Button as ComponentType<Record<string, unknown>>;
import { useTank } from '@/contexts/TankContext';
import { tasksApi, type TaskCompletionUpdate } from '@/api/tasks';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type {
  TaskDefinitionCreate,
  TaskDefinitionUpdate,
  CompleteTaskData,
  SnoozeOptions,
} from '@/types/task';

/**
 * Hook to fetch task types.
 * Types are static and rarely change, so we use a long staleTime.
 */
export function useTaskTypes() {
  return useQuery({
    queryKey: ['task-types'],
    queryFn: () => tasksApi.getTypes(),
    staleTime: CACHE_TIMES.CONFIG,
    meta: { persist: true },
  });
}

/**
 * Hook to fetch task definitions for the current tank.
 * Supports optional status and task_type filters.
 */
export function useTaskDefinitions(params?: { status?: string; task_type?: string }) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['task-definitions', selectedTank?.id, params],
    queryFn: () => tasksApi.listDefinitions(selectedTank!.id, params),
    enabled: !!selectedTank?.id,
  });
}

/**
 * Hook to fetch pending task instances for the current tank.
 */
export function useTaskInstances() {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['task-instances', selectedTank?.id],
    queryFn: () => tasksApi.listInstances(selectedTank!.id),
    enabled: !!selectedTank?.id,
  });
}

/**
 * Hook to fetch task completions (history) for the current tank.
 * Supports optional date range, task_type, and limit filters.
 */
export function useTaskCompletions(params?: {
  start_date?: string;
  end_date?: string;
  task_type?: string;
  limit?: number;
}) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['task-completions', selectedTank?.id, params],
    queryFn: () => tasksApi.listCompletions(selectedTank!.id, params),
    enabled: !!selectedTank?.id,
  });
}

/**
 * Hook to fetch task completions for a specific task definition.
 * Useful for displaying completion history in the task edit modal.
 */
export function useTaskCompletionsByDefinition(definitionId: string | undefined) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['task-completions', selectedTank?.id, 'definition', definitionId],
    queryFn: () => tasksApi.listCompletions(selectedTank!.id, { definition_id: definitionId }),
    enabled: !!selectedTank?.id && !!definitionId,
  });
}

/**
 * Hook to create a new task definition.
 * Invalidates task queries on success and shows notification.
 */
export function useCreateTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TaskDefinitionCreate) => tasksApi.createDefinition(selectedTank!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Task created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to update an existing task definition.
 * Invalidates task queries on success and shows notification.
 */
export function useUpdateTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TaskDefinitionUpdate }) =>
      tasksApi.updateDefinition(selectedTank!.id, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Task updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to cancel a task definition (soft delete).
 * Invalidates task queries on success and shows notification.
 */
export function useCancelTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definitionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.cancelDefinition(selectedTank.id, definitionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Task cancelled',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to cancel task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to undo a task completion.
 * Reverts the instance back to pending, removes auto-generated next instance
 * for recurring tasks, and deletes the completion record.
 */
export function useUndoCompletion() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (completionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.undoCompletion(selectedTank.id, completionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['dosing-events', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', selectedTank?.id] });
      notifications.show({
        title: 'Undone',
        message: 'Task moved back to due list',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to undo completion',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to complete a task instance.
 * Invalidates task queries on success and shows notification with Undo button.
 */
export function useCompleteTask() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  const invalidateAfterUndo = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['dosing-events', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', selectedTank?.id] });
  }, [queryClient, selectedTank?.id]);

  return useMutation({
    mutationFn: ({ instanceId, data }: { instanceId: string; data: CompleteTaskData }) =>
      tasksApi.completeInstance(selectedTank!.id, instanceId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
      // Dosing task completions create dosing events - refresh dosing data
      if (variables.data.chemical && variables.data.amount != null) {
        queryClient.invalidateQueries({ queryKey: ['dosing-events', selectedTank?.id] });
        queryClient.invalidateQueries({ queryKey: ['dashboard', selectedTank?.id] });
      }

      const completionId = data.id;
      const notificationId = `task-completed-${completionId}`;
      const tankId = selectedTank!.id;

      const handleUndo = async () => {
        notifications.hide(notificationId);
        try {
          await tasksApi.undoCompletion(tankId, completionId);
          invalidateAfterUndo();
          notifications.show({
            title: 'Undone',
            message: 'Task moved back to due list',
            color: 'blue',
          });
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to undo completion',
            color: 'red',
          });
        }
      };

      notifications.show({
        id: notificationId,
        title: 'Task completed!',
        message: createElement(
          Group,
          { gap: 'xs', justify: 'space-between', wrap: 'nowrap' },
          createElement(TextC, { size: 'sm' }, 'Marked as done'),
          createElement(
            ButtonC,
            {
              size: 'compact-xs',
              variant: 'white',
              color: 'green',
              onClick: handleUndo,
            },
            'Undo'
          )
        ),
        color: 'green',
        autoClose: 10000,
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to complete task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to log a completion against a task definition (from All Tasks view).
 * If a pending instance exists, marks it complete and schedules next.
 * Invalidates all task queries on success.
 */
export function useLogCompletion() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  const invalidateAfterUndo = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['dosing-events', selectedTank?.id] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', selectedTank?.id] });
  }, [queryClient, selectedTank?.id]);

  return useMutation({
    mutationFn: ({
      definitionId,
      data,
    }: {
      definitionId: string;
      data: { completed_at?: string; notes?: string };
    }) => tasksApi.logCompletion(selectedTank!.id, definitionId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });

      const completionId = data.id;
      const notificationId = `task-logged-${completionId}`;
      const tankId = selectedTank!.id;

      const handleUndo = async () => {
        notifications.hide(notificationId);
        try {
          await tasksApi.undoCompletion(tankId, completionId);
          invalidateAfterUndo();
          notifications.show({
            title: 'Undone',
            message: 'Task moved back to due list',
            color: 'blue',
          });
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to undo completion',
            color: 'red',
          });
        }
      };

      notifications.show({
        id: notificationId,
        title: 'Completion logged!',
        message: createElement(
          Group,
          { gap: 'xs', justify: 'space-between', wrap: 'nowrap' },
          createElement(TextC, { size: 'sm' }, 'Marked as done'),
          createElement(
            ButtonC,
            {
              size: 'compact-xs',
              variant: 'white',
              color: 'green',
              onClick: handleUndo,
            },
            'Undo'
          )
        ),
        color: 'green',
        autoClose: 10000,
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to log completion',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to snooze a task instance until a later time.
 * Invalidates task queries on success and shows notification.
 */
export function useSnoozeTask() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ instanceId, data }: { instanceId: string; data: SnoozeOptions }) =>
      tasksApi.snoozeInstance(selectedTank!.id, instanceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Rescheduled',
        message: 'Task due date updated',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to snooze task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to skip a task instance.
 * Invalidates task queries on success and shows notification.
 */
export function useSkipTask() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instanceId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.skipInstance(selectedTank.id, instanceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Skipped',
        message: 'Task skipped',
        color: 'gray',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to skip task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to pause a task definition (stop scheduling).
 */
export function usePauseTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definitionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.pauseDefinition(selectedTank.id, definitionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Paused',
        message: 'Task scheduling paused',
        color: 'yellow',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to pause task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to resume a paused task definition.
 */
export function useResumeTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definitionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.resumeDefinition(selectedTank.id, definitionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Resumed',
        message: 'Task scheduling resumed',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to resume task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to archive a task definition.
 */
export function useArchiveTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definitionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.archiveDefinition(selectedTank.id, definitionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      notifications.show({
        title: 'Archived',
        message: 'Task archived — history preserved',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to archive task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to restore an archived task definition.
 */
export function useRestoreTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definitionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.restoreDefinition(selectedTank.id, definitionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      notifications.show({
        title: 'Restored',
        message: 'Task restored from archive',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to restore task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to permanently delete a task definition and all history.
 */
export function useDeleteTaskDefinition() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (definitionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.deleteDefinition(selectedTank.id, definitionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-definitions', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-instances', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
      notifications.show({
        title: 'Deleted',
        message: 'Task and all history permanently deleted',
        color: 'red',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete task',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to update a task completion.
 */
export function useUpdateTaskCompletion() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ completionId, data }: { completionId: string; data: TaskCompletionUpdate }) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.updateCompletion(selectedTank.id, completionId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Completion updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update completion',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to delete a task completion.
 */
export function useDeleteTaskCompletion() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (completionId: string) => {
      if (!selectedTank?.id) throw new Error('No tank selected');
      return tasksApi.deleteCompletion(selectedTank.id, completionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-completions', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Completion deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete completion',
        color: 'red',
      });
    },
  });
}
